import { useState } from 'react';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import type { PasskeyInfo } from '@aegislab/shared';
import { Button } from '@/shared/ui/atoms';
import { Input } from '@/shared/ui/atoms';
import {
  usePasskeys,
  useRegisterPasskey,
  useDeletePasskey,
  useRenamePasskey,
} from '../../model/usePasskeys';
import { getApiErrorMessage } from '../../../../shared/lib/getApiErrorMessage';
import { formatDateLocale } from '@/shared/lib/format';
import * as s from './PasskeyManageModal.css';

function DeviceIcon({ type }: { type: string }) {
  return <span>{type === 'multiDevice' ? '\uD83D\uDD11' : '\uD83D\uDCF1'}</span>;
}

/**
 * 패스키 등록·목록·이름 변경·삭제 패널. 자기 모달을 갖지 않는다 —
 * 상태창의 "비밀번호 · 패스키" 모달이 두 번째 세그먼트로 품는다 (StatusHud.passkeyPanelSlot).
 */
export function PasskeyManagePanel() {
  const { data: passkeys = [], isLoading } = usePasskeys();
  const registerMutation = useRegisterPasskey();
  const deleteMutation = useDeletePasskey();
  const { rename } = useRenamePasskey();

  const [newName, setNewName] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const supported = browserSupportsWebAuthn();

  const handleRegister = async (crossPlatform?: boolean) => {
    setError(null);
    setIsRegistering(true);
    try {
      await registerMutation.mutateAsync({
        friendlyName: newName || undefined,
        crossPlatform,
      });
      setNewName('');
      setIsRegistering(false);
    } catch (err) {
      setIsRegistering(false);
      // 사용자가 OS 다이얼로그를 취소한 경우(NotAllowedError)는 인라인 에러를 띄우지 않는다.
      if (err instanceof Error) {
        const isCancel =
          err.name === 'NotAllowedError' ||
          err.message.includes('인증이 거부되었거나 시간이 초과') ||
          err.message.includes('패스키 등록이 취소');
        if (isCancel) return;
      }
      setError(getApiErrorMessage(err, '패스키 등록에 실패했습니다'));
    }
  };

  const handleDelete = async (passkeyId: string) => {
    try {
      await deleteMutation.mutateAsync(passkeyId);
    } catch (err) {
      setError(getApiErrorMessage(err, '패스키 삭제에 실패했습니다'));
    }
  };

  const handleRenameSubmit = async (passkeyId: string) => {
    if (!editName.trim()) return;
    try {
      await rename(passkeyId, editName.trim());
      setEditingId(null);
      setEditName('');
    } catch (err) {
      setError(getApiErrorMessage(err, '패스키 이름 변경에 실패했습니다'));
    }
  };

  const startEditing = (passkey: PasskeyInfo) => {
    setEditingId(passkey.id);
    setEditName(passkey.friendlyName || '');
  };

  return (
    <div className={s.content}>
      {!supported ? (
        <div className={s.unsupportedNotice}>이 브라우저는 패스키를 지원하지 않습니다.</div>
      ) : (
        <>
          {/* Register Section */}
          <div className={s.registerSection}>
            <div className={s.nameInput}>
              <Input
                fullWidth
                placeholder="패스키 이름 (선택)"
                value={newName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
                disabled={isRegistering}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  void handleRegister();
                }}
                loading={isRegistering}
                disabled={isRegistering}
              >
                등록
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void handleRegister(true);
                }}
                loading={isRegistering}
                disabled={isRegistering}
              >
                다른 기기로 등록
              </Button>
            </div>
            {error && <span className={s.errorText}>{error}</span>}
          </div>

          {/* Passkey List */}
          {isLoading ? (
            <div className={s.emptyState}>불러오는 중...</div>
          ) : passkeys.length === 0 ? (
            <div className={s.emptyState}>등록된 패스키가 없습니다. 위에서 등록해주세요.</div>
          ) : (
            <div className={s.passkeyList}>
              {passkeys.map((pk) => (
                <div key={pk.id} className={s.passkeyCard}>
                  <div className={s.passkeyInfo}>
                    {editingId === pk.id ? (
                      <div className={s.nameInput}>
                        <Input
                          fullWidth
                          value={editName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setEditName(e.target.value)
                          }
                          onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter') void handleRenameSubmit(pk.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                        />
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            void handleRenameSubmit(pk.id);
                          }}
                        >
                          저장
                        </Button>
                      </div>
                    ) : (
                      <span className={s.passkeyName}>
                        {pk.friendlyName || `패스키 ${pk.id.slice(0, 8)}`}
                      </span>
                    )}
                    <div className={s.passkeyMeta}>
                      <span className={s.deviceBadge}>
                        <DeviceIcon type={pk.deviceType} />
                        {pk.deviceType === 'multiDevice' ? '동기화됨' : '기기 전용'}
                      </span>
                      <span>{formatDateLocale(pk.createdAt)}</span>
                    </div>
                  </div>
                  <div className={s.passkeyActions}>
                    <button
                      type="button"
                      className={s.iconButton}
                      onClick={() => startEditing(pk)}
                      aria-label="패스키 이름 변경"
                      title="이름 변경"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className={s.deleteButton}
                      onClick={() => {
                        void handleDelete(pk.id);
                      }}
                      aria-label="패스키 삭제"
                      title="삭제"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
